/**
 * Canadian French locale for extensions-ui.
 * Export is the "extensions" subtree: host apps merge this into their i18n
 * resources as translation.extensions so that t('extensions.list.heading') etc. resolve.
 */
import type { ExtensionsLocale } from './en'

export const extensionsFrCa = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `Annuler`,
    delete: `Supprimer`,
    save: `Enregistrer`,
    apply: `Appliquer`,
    edit: `Modifier`,
    actions: `Actions`,
    add: `Ajouter`,
    all: `Tous`,
    loading: `Chargement...`,
    noChange: `Aucun changement`,
    none: `Aucun`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    pageLabel: `Toutes les extensions`,
    heading: `Extensions`,
    linesHeading: `Lignes`,
    searchPlaceholder: `Rechercher par nom ou numéro`,
    linesSearchPlaceholder: `Rechercher par nom, numéro, profil assigné ou emplacement`,
    loadingExtensions: `Chargement des extensions...`,
    noMatch: `Aucune extension ne correspond à votre recherche`,
    noExtensions: `Aucune extension trouvée`,
    noMatchHint: `Essayez un terme de recherche différent.`,
    noExtensionsHint: `Il n'y a aucune extension pour ce PBX.`,
    activateAll: `Tout activer`,
    deactivateAll: `Tout désactiver`,
    massEdit: `Modification en lot`,
    linesSelected: `{{count}} ligne{{suffix}} sélectionnée{{suffix}}`,
  },

  // ---------------------------------------------------------------------------
  // Column headers
  // ---------------------------------------------------------------------------
  columns: {
    number: `Numéro`,
    name: `Nom`,
    type: `Type`,
    created: `Créé`,
    updated: `Mis à jour`,
    lastUpdated: `Dernière mise à jour`,
    assignedUser: `Utilisateur assigné`,
    assignedProfile: `Profil assigné`,
    location: `Emplacement`,
    actions: `Actions`,
  },

  // ---------------------------------------------------------------------------
  // Row actions & badges
  // ---------------------------------------------------------------------------
  row: {
    inactive: `Inactif`,
    edit: `Modifier`,
    delete: `Supprimer`,
    viewCallMetrics: `Voir les métriques d'appels`,
    activate: `Activer`,
    deactivate: `Désactiver`,
    extensionFallback: `Extension {{id}}`,
    selectAllLines: `Sélectionner toutes les lignes`,
    selectLine: `Sélectionner {{name}}`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `Supprimer l'extension`,
    confirmation: `Êtes-vous sûr de vouloir supprimer « {{displayName}} » ? Cette action est irréversible.`,
    unnamed: `Extension sans nom`,
    reassignLabel: `Réassigner les références à`,
    deleteWithoutReassigning: `Supprimer sans réassigner`,
  },

  // ---------------------------------------------------------------------------
  // Set active state dialog (single)
  // ---------------------------------------------------------------------------
  activeStateDialog: {
    activateTitle: `Activer l'extension`,
    deactivateTitle: `Désactiver l'extension`,
    body: `Vous êtes sur le point de {{action}} {{displayName}}, ce qui provoquera le redémarrage de tous les appareils utilisant cette extension comme extension de ligne principale.`,
    unnamedLine: `Ligne sans nom`,
    confirmPrompt: `Êtes-vous sûr de vouloir continuer ?`,
    setActive: `Définir comme actif`,
    setInactive: `Définir comme inactif`,
  },

  // ---------------------------------------------------------------------------
  // Bulk active state dialog
  // ---------------------------------------------------------------------------
  bulkActiveDialog: {
    activateTitle: `Activer toutes les extensions`,
    deactivateTitle: `Désactiver toutes les extensions`,
    body: `Vous êtes sur le point de {{action}} toutes les extensions, ce qui provoquera le redémarrage de tous les appareils utilisant ces extensions comme extension de ligne principale.`,
    confirmPrompt: `Êtes-vous sûr de vouloir continuer ?`,
    activateAll: `Tout activer`,
    deactivateAll: `Tout désactiver`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `Modification en lot des lignes`,
    description: `Mettez à jour les champs suivants pour {{count}} ligne{{suffix}}. Laissez « Aucun changement » pour conserver la valeur actuelle.`,
    outboundCallerId: `ID de l'appelant sortant`,
    localPresence: `Présence locale`,
    preferredCodec: `Codec préféré`,
    location: `Emplacement`,
    locationPlaceholder: `Aucun changement ou tapez pour rechercher`,
    locationSearchLabel: `Recherche d'emplacement`,
    loadingLocations: `Chargement des emplacements\u2026`,
    noLocationsFound: `Aucun emplacement trouvé`,
    typeToSearchLocations: `Tapez pour rechercher des emplacements`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `Résultats par page :`,
    previousPage: `Page précédente`,
    nextPage: `Page suivante`,
    all: `Tous`,
    jumpToPage: `Aller à la page {{page}}`,
    searchExtensions: `Rechercher des extensions`,
    toggleFilters: `Basculer les filtres`,
    showingAll: `Affichage de tous les {{total}} résultats`,
    showingRange: `Affichage de {{start}} à {{end}} sur {{total}} résultats`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deleteSuccess: `Extension supprimée avec succès.`,
    deleteFailed: `Échec de la suppression`,
    deleteFailedDescription: `Impossible de supprimer l'extension. Veuillez réessayer.`,
    success: `Succès`,
    failed: `Échec`,
    activeStateSuccess: `Ligne définie comme {{state}}.`,
    activeStateFailed: `Impossible de mettre à jour l'état actif de la ligne.`,
    bulkSuccess: `{{count}} ligne{{suffix}} définie{{suffix}} comme {{state}}.`,
    partialFailure: `Échec partiel`,
    partialFailureDescription: `{{succeeded}} réussis, {{failed}} échoués. Certaines lignes n'ont peut-être pas été mises à jour.`,
    massEditSuccess: `{{count}} ligne{{suffix}} mise{{suffix}} à jour.`,
    massEditFailed: `Échec de la modification en lot`,
    massEditFailedDescription: `Impossible de mettre à jour les lignes sélectionnées. Réessayez.`,
  },

  // ---------------------------------------------------------------------------
  // Extension type labels
  // ---------------------------------------------------------------------------
  types: {
    line: `Ligne`,
    ringGroup: `Groupe de sonnerie`,
    virtualFax: `Télécopieur virtuel`,
    conferenceRoom: `Salle de conférence`,
    switchboard: `Tableau de bord`,
    sharedVoicemail: `Boîte vocale partagée`,
    callQueue: `File d'attente d'appels`,
  },

  // ---------------------------------------------------------------------------
  // View mode labels
  // ---------------------------------------------------------------------------
  viewModes: {
    extensions: `Extensions`,
    lines: `Lignes`,
    ringGroups: `Groupes de sonnerie`,
    callQueues: `Files d'attente d'appels`,
    conferenceRooms: `Salles de conférence`,
    virtualFax: `Télécopieur virtuel`,
    sharedVoicemail: `Boîte vocale partagée`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    title: `Filtre`,
    status: `Statut :`,
    assignedUser: `Utilisateur assigné :`,
    assignedProfile: `Profil assigné :`,
    location: `Emplacement :`,
    active: `Actif`,
    inactive: `Inactif`,
    all: `Tous`,
    clearFilters: `Effacer les filtres`,
    apply: `Appliquer`,
  },

  placeholder: {
    notImplemented: `La page d'édition de {{typeLabel}} n'est pas encore implémentée.`,
    backToList: `Retour à {{listLabel}}`,
  },

  edit: {
    validationError: `Erreur de validation`,
    reviewRequiredFields: `Veuillez vérifier les champs obligatoires et réessayer.`,
    emailFormatInvalid: `Format de courriel incorrect. Assurez-vous qu'il n'y a pas d'espaces. Séparez plusieurs adresses par des virgules.`,
    nameNumberRequired: `Le nom et le numéro sont requis.`,
    reservedNumber: `{{number}} est réservé. Veuillez sélectionner un autre numéro.`,
    createFailed: `Échec de création`,
    updateFailed: `Échec de mise à jour`,
    breadcrumbAdd: `Ajouter {{typeLabel}}`,
    breadcrumbEdit: `Modifier {{typeLabel}} {{value}}`,
    backToList: `← Retour à {{listLabel}}`,
    wrongPbx: `Cette extension n'est pas dans le PBX sélectionné`,
    wrongPbxDescription: `Cette extension n'appartient pas au PBX sélectionné.`,
    openInPbx: `Ouvrir dans {{pbxName}}`,
    loadFailed: `Impossible de charger cette extension`,
    options: `Options`,
    saveChanges: `Enregistrer les modifications`,
    omitFromDirectory: `Omettre du répertoire`,
    nameRequired: `Le nom est requis`,
    numberRequired: `Le numéro est requis`,
    extensionNumberPlaceholder: `Numéro d'extension`,
    yes: `Oui`,
    no: `Non`,
    members: `Membres`,
    membersNoun: `membres`,
    userGroups: `Groupes d'utilisateurs`,
    userGroupsNoun: `groupes d'utilisateurs`,
    holdMusic: `Musique d'attente`,
    line: {
      requiredFields: `Le nom, le numéro, l'emplacement et le profil assigné sont requis.`,
      selectLocation: `Sélectionnez un emplacement`,
      notType: `Ce n'est pas une ligne`,
      pbxUuid: `UUID du PBX`,
      pbxName: `PBX`,
      sipSecret: `Secret SIP`,
      sipSecretReset: `Secret SIP réinitialisé`,
      reset: `Réinitialiser`,
      extensionUuid: `UUID de l'extension`,
      dialNow: `Composer maintenant`,
      dialNowCleared: `Dial-now effacé`,
      clear: `Effacer`,
      assignedProfileMobileDevices: `Appareils mobiles du profil assigné`,
      addTitle: `Ajouter une ligne`,
      title: `Ligne`,
      voicemailOptions: `Messagerie vocale`,
      outDialExtension: `Extension OutDial`,
      outDialHelp: `Aide extension OutDial`,
      outDialHelpNo: `Non : Cette extension agit comme une extension régulière.`,
      outDialHelpYes: `Oui : Cette extension redirige uniquement vers le numéro de renvoi.`,
      outDialHelpBeAware: `À savoir :`,
      outDialHelpWarningOne: `Le système ne peut pas récupérer un appel une fois transféré vers un numéro externe.`,
      outDialHelpWarningTwo: `Vous ne pouvez pas utiliser la messagerie vocale système avec une extension OutDial.`,
      outDialEnabledHint: `OutDial est activé. Les appels sont renvoyés vers le numéro de renvoi; l'identifiant d'appelant sortant s'applique toujours sur la branche sortante.`,
      outDialDisabledHint: `OutDial est désactivé. Les options d'appel sortant s'appliquent.`,
      loadingProfiles: `Chargement des profils…`,
      voicemailEnabled: `Boîte vocale activée`,
      forwardingNumber: `Numéro de renvoi (OutDial)`,
      localAreaCode: `Indicatif régional local`,
      localAreaCodeInvalid: `L'indicatif régional est invalide. {{areaCode}} n'est pas autorisé pour l'indicatif régional local.`,
      localAreaCodeNotAllowed: `L'indicatif régional {{areaCode}} n'est pas autorisé pour l'indicatif régional local.`,
      outboundCallerIdRequired: `Veuillez sélectionner un ID d'appelant sortant.`,
      outboundCallerIdNoOptions: `Ce PBX n'a aucun numéro de téléphone configuré. Ajoutez un numéro au PBX avant de modifier cette ligne.`,
      outboundCallerIdMissingSavedValue: `Cette ligne n'a pas d'ID d'appelant sortant valide. Veuillez en sélectionner un et enregistrer avant de continuer.`,
      expandDetails: `Afficher les détails`,
      collapseDetails: `Masquer les détails`,
      activate: `Activer`,
      deactivate: `Désactiver`,
      findMe: {
        title: `Me trouver`,
        mode: `Me trouver`,
        number: `Numéro Me trouver`,
        delay: `Me trouver après`,
        modeOptions: {
          '0': `Désactivé`,
          '1': `Activé`,
          '2': `Faire sonner tout le monde`,
        },
        delayOptions: {
          '0': `Immédiatement`,
          '5': `5 secondes`,
          '10': `10 secondes`,
          '15': `15 secondes`,
        },
        numberRequired: `Un numéro Me trouver est requis lorsque Me trouver est activé.`,
        numberInvalid: `Le numéro Me trouver doit contenir uniquement des chiffres et un + initial facultatif (maximum 15 chiffres).`,
      },
      callRecording: `Enregistrement d'appels`,
      callRecordingOptions: {
        '0': `Non`,
        '1': `Oui`,
        '2': `Défini par le système`,
      },
      dedicatedFax: `Télécopie dédiée`,
      inCallFeatureCodes: `Codes de fonction en appel activés`,
      createButton: `Créer la ligne`,
      saveAndCreateAnother: `Enregistrer et créer une autre`,
      details: `Détails de la ligne`,
      codecOpus: `Idéal pour Internet lent (OPUS) Prend en charge : Téléphones Web, Yealink`,
      codecPcmu: `Standard (PCMU) Prend en charge : Tous les types de téléphones`,
      codecG722: `Audio HD (G722) Prend en charge : Yealink, Cisco`,
      codecG729: `OBSOLÈTE - Bon pour Internet lent (G729) Prend en charge : Yealink, Cisco`,
    },
    ringGroup: {
      ringing: `Sonnerie`,
      created: `Groupe de sonnerie créé`,
      updated: `Groupe de sonnerie mis à jour`,
      saveFailed: `Échec de l'enregistrement du groupe de sonnerie`,
      notType: `Ce n'est pas un groupe de sonnerie`,
      notTypeDescription: `Cette extension n'est pas un groupe de sonnerie. Utilisez la liste des extensions pour modifier d'autres types.`,
      namePlaceholder: `Nom du groupe de sonnerie`,
      ringStrategy: `Stratégie de sonnerie`,
      ringAll: `Sonner tout`,
      pagingGroup: `Groupe de radiomessagerie`,
      ringbackAudio: `Audio de retour de sonnerie`,
      createButton: `Créer un groupe de sonnerie`,
      updateButton: `Mettre à jour le groupe de sonnerie`,
    },
    callQueue: {
      created: `File d'attente créée`,
      updated: `File d'attente mise à jour`,
      saveFailed: `Échec de l'enregistrement de la file d'attente`,
      notType: `Ce n'est pas une file d'attente`,
      queueSettings: `Paramètres de file`,
      ringStrategy: `Stratégie de sonnerie`,
      strategyRingAll: `Sonner tout`,
      strategyRoundRobin: `Tour de rôle (Round Robin)`,
      strategyLongestIdle: `Plus inactif`,
      strategyRandom: `Aléatoire`,
      strategyLeastTalkTime: `Moins de temps de conversation`,
      strategyFewestCalls: `Moins d'appels`,
      preCallAnnouncement: `Annonce avant appel`,
      maxNoAnswer: `Maximum sans réponse (tentatives)`,
      busyDelayTime: `Délai occupé (secondes)`,
      rejectDelayTime: `Délai rejet (secondes)`,
      wrapUpTime: `Temps de clôture (secondes)`,
      noAnswerDelayTime: `Délai sans réponse (secondes)`,
      maxRingDuration: `Durée max de sonnerie (secondes)`,
      maxCallersInQueue: `Appelants max en file`,
      maxWaitTime: `Temps d'attente max (secondes)`,
      timeoutDestination: `Destination d'expiration`,
      escapeDestination: `Destination d'échappement`,
      destinationSearchPlaceholder: `Rechercher des postes...`,
      destinationNoMatches: `Aucun poste ne correspond à cette recherche.`,
      destinationCurrentUnlisted: `{{value}} (actuel)`,
      periodicAnnouncements: `Annonces périodiques`,
      positionAnnouncementFrequency: `Fréquence d'annonce de position`,
      positionAnnouncementMessage: `Message d'annonce de position`,
      queuePosition: `Position dans la file`,
      agentsNoun: `agents`,
      newButton: `Nouvelle file d'attente`,
      updateButton: `Mettre à jour la file d'attente`,
    },
    conferenceRoom: {
      moderatorPinValidation: `Le NIP modérateur doit contenir exactement 4 chiffres.`,
      conferencePinValidation: `Le NIP conférence doit contenir exactement 4 chiffres.`,
      holdMusicRequired: `La musique d'attente est requise.`,
      created: `Salle de conférence créée`,
      updated: `Salle de conférence mise à jour`,
      saveFailed: `Échec de l'enregistrement de la salle de conférence`,
      notType: `Ce n'est pas une salle de conférence`,
      moderatorRequired: `Modérateur requis`,
      moderatorPin: `NIP modérateur`,
      userPinRequired: `NIP utilisateur requis`,
      conferencePin: `NIP conférence`,
      defaultMuted: `Muet par défaut`,
      announceNewParticipants: `Annoncer les nouveaux participants`,
      conferenceGreeting: `Accueil de conférence`,
      selectHoldMusic: `Sélectionner la musique d'attente`,
      createButton: `Créer une salle de conférence`,
      updateButton: `Mettre à jour la salle de conférence`,
    },
    virtualFax: {
      invalidEmail: `Veuillez entrer une adresse courriel valide.`,
      passwordsDoNotMatch: `Les mots de passe ne correspondent pas.`,
      passwordMinLength: `Le mot de passe du fax sécurisé doit contenir au moins 8 caractères.`,
      created: `Fax virtuel créé`,
      updated: `Fax virtuel mis à jour`,
      saveFailed: `Échec de l'enregistrement du fax virtuel`,
      secureFaxLinkResent: `Lien de fax sécurisé renvoyé`,
      resendFailed: `Échec du renvoi`,
      resendSaveFailed: `Échec du renvoi du lien de fax sécurisé`,
      notType: `Ce n'est pas un fax virtuel`,
      receivingOptions: `Options de réception`,
      receivingEmailDestination: `Destination courriel de réception`,
      secureFax: `Fax sécurisé`,
      download: `Téléchargement`,
      singleDownload: `Téléchargement unique`,
      download5: `5 téléchargements`,
      download10: `10 téléchargements`,
      download15: `15 téléchargements`,
      failedLoginRetries: `Tentatives de mot de passe échouées`,
      retries5: `5 tentatives`,
      retries10: `10 tentatives`,
      retries15: `15 tentatives`,
      linkExpiresIn: `Le lien expire dans`,
      day1: `1 jour`,
      day2: `2 jours`,
      day3: `3 jours`,
      day7: `7 jours`,
      day15: `15 jours`,
      day30: `30 jours`,
      changePasswordOptional: `Changer le mot de passe du fax sécurisé (optionnel)`,
      userGroupsDescription: `Choisissez quels groupes d'utilisateurs auront accès à ce fax virtuel.`,
      sending: `Envoi…`,
      resendSecureFaxLink: `Renvoyer le lien de fax sécurisé`,
      createButton: `Créer un télécopieur virtuel`,
      updateButton: `Mettre à jour le télécopieur virtuel`,
    },
    shared: {
      generalOptions: `Options générales`,
      voicemailTimeZone: `Fuseau horaire de la boîte vocale`,
      voicemailPassword: `Mot de passe de la boîte vocale`,
      playVoicemailInstructions: `Lire les instructions de boîte vocale`,
      notificationOptions: `Options de notification`,
      sendEmailNotifications: `Envoyer des notifications courriel`,
      messageEmailAddress: `Adresse courriel des messages`,
      emailPlaceholder: `courriel@exemple.com`,
      attachVoicemailMessage: `Joindre le message vocal`,
      deleteMessageAfterSend: `Supprimer le message après envoi`,
      outgoingMessage: `Message sortant`,
      useSystemGreetings: `Utiliser les salutations système`,
      customGreeting: `Salutation personnalisée`,
    },
    sharedVoicemail: {
      created: `Boîte vocale partagée créée`,
      updated: `Boîte vocale partagée mise à jour`,
      saveFailed: `Échec de l'enregistrement de la boîte vocale partagée`,
      notType: `Ce n'est pas une boîte vocale partagée`,
      userGroupsDescription: `Choisissez quels groupes d'utilisateurs auront accès à cette boîte vocale partagée.`,
      createButton: `Créer une boîte vocale partagée`,
      updateButton: `Mettre à jour la boîte vocale partagée`,
    },
  },

  updateLocation: {
    title: `Mettre à jour l'emplacement`,
    description: `Mettre à jour l'emplacement pour {{count}} ligne{{suffix}}?`,
    confirm: `Mettre à jour l'emplacement`,
  },

  pickList: {
    assignedWithLimit: `{{itemNoun}} assignés: {{count}} / {{max}}`,
    assigned: `{{itemNoun}} assignés: {{count}}`,
    noneAssigned: `Aucun {{itemNoun}} ajouté pour l'instant.`,
    remove: `Retirer`,
    available: `{{itemNoun}} disponibles: {{count}}`,
    searchAvailable: `Rechercher parmi les {{itemNoun}} disponibles`,
    maxReached: `Maximum de {{max}} {{itemNoun}} atteint.`,
    noneAvailable: `Aucun {{itemNoun}} disponible.`,
  },

  localPresence: {
    0: `Désactivé`,
    1: `Groupe 1`,
    2: `Groupe 2`,
    3: `Groupe 3`,
    4: `Groupe 4`,
    5: `Groupe 5`,
    6: `Groupe 6`,
  },

  codecOptions: {
    opus: `Idéal pour Internet lent (OPUS) — Prend en charge : Téléphones Web, Yealink`,
    pcmu: `Standard (PCMU) — Prend en charge : Tous les types de téléphones`,
    g722: `Audio HD (G722) — Prend en charge : Yealink, Cisco`,
    g729: `OBSOLÈTE — Bon pour Internet lent (G729) — Prend en charge : Yealink, Cisco`,
    default: `Valeur par défaut du système`,
  },

  timezones: {
    america_new_york: `Eastern Time`,
    america_chicago: `Central Time`,
    america_denver: `Mountain Time`,
    america_los_angeles: `Pacific Time`,
    america_phoenix: `Arizona`,
    america_anchorage: `Alaska Time`,
    pacific_honolulu: `Hawaii Time`,
    america_toronto: `Toronto (Eastern)`,
    america_vancouver: `Heure du Pacifique - Colombie-Britannique (PCT)`,
    europe_london: `London (GMT/BST)`,
    europe_paris: `Paris (CET)`,
    australia_sydney: `Sydney (AEST)`,
    utc: `UTC`,
  },

  // ---------------------------------------------------------------------------
  // Create extension options
  // ---------------------------------------------------------------------------
  createOptions: {
    add: `Ajouter`,
    addLine: `Ajouter une ligne`,
    addRingGroup: `Ajouter un groupe de sonnerie`,
    addCallQueue: `Ajouter une file d'attente d'appels`,
    addConferenceRoom: `Ajouter une salle de conférence`,
    addVirtualFax: `Ajouter un télécopieur virtuel`,
    addSharedVoicemail: `Ajouter une boîte vocale partagée`,
  },

  // ---------------------------------------------------------------------------
  // Ordering options
  // ---------------------------------------------------------------------------
  ordering: {
    lastUpdatedNewest: `Dernière mise à jour (plus récent)`,
    lastUpdatedOldest: `Dernière mise à jour (plus ancien)`,
    createdNewest: `Créé (plus récent)`,
    createdOldest: `Créé (plus ancien)`,
    nameAZ: `Nom (A\u2013Z)`,
    nameZA: `Nom (Z\u2013A)`,
    numberAsc: `Numéro (asc)`,
    numberDesc: `Numéro (desc)`,
  },

  // ---------------------------------------------------------------------------
  // Slug-to-label mappings (for breadcrumbs)
  // ---------------------------------------------------------------------------
  slugLabels: {
    line: `Ligne`,
    ringGroup: `Groupe de sonnerie`,
    callQueue: `File d'attente d'appels`,
    conferenceRoom: `Salle de conférence`,
    virtualFax: `Télécopieur virtuel`,
    sharedVoicemail: `Boîte vocale partagée`,
  },

  // ---------------------------------------------------------------------------
  // List labels for type slugs
  // ---------------------------------------------------------------------------
  listLabels: {
    lines: `Lignes`,
    ringGroups: `Groupes de sonnerie`,
    callQueues: `Files d'attente d'appels`,
    conferenceRooms: `Salles de conférence`,
    virtualFax: `Télécopieur virtuel`,
    sharedVoicemail: `Boîte vocale partagée`,
  },
} satisfies ExtensionsLocale
